import { useQuery } from "@tanstack/react-query";
import { create } from "zustand";
import { api } from "./api";
import type { Group } from "./types";

export interface WorkerInfo {
  version?: string;
  name?: string;
  host?: string;
  os?: string;
  agents?: string[];
  roots?: string[];
  folders_root?: string | null;
  exec?: boolean;
}

export interface Worker {
  id: string;
  name: string;
  owner_id: string;
  mine: boolean;
  online: boolean;
  info: WorkerInfo;
  exec_approval: boolean;
  groups: Group[];
  last_seen_at: string | null;
}

export interface AgentSession {
  id: string;
  worker_id: string | null;
  worker_name: string | null;
  worker_online: boolean;
  kind: string;
  cwd: string;
  title: string;
  status: "starting" | "idle" | "busy" | "exited" | "error";
  state: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

// pi RPC records (only the fields the UI uses)
export type Block =
  | { type: "text"; text: string }
  | { type: "thinking"; thinking: string }
  | { type: "toolCall"; id: string; name: string; arguments: unknown }
  | { type: "image"; data: string; mimeType: string };

export interface AgentRecord {
  type: string;
  [key: string]: any;
}

export function useWorkers() {
  return useQuery({ queryKey: ["workers"], queryFn: () => api.get<Worker[]>("/api/workers") });
}

/** The assistant message being streamed right now, per session, built from message_update deltas. */
interface Partial {
  blocks: Block[];
}

interface AgentLiveStore {
  partial: Record<string, Partial | undefined>;
  apply: (sessionId: string, rec: AgentRecord) => void;
}

export const useAgentLive = create<AgentLiveStore>((set, get) => ({
  partial: {},
  apply: (sid, rec) => {
    if (rec.type === "message_start" && rec.message?.role === "assistant") {
      set((s) => ({ partial: { ...s.partial, [sid]: { blocks: [] } } }));
      return;
    }
    if (rec.type === "message_end" || rec.type === "agent_settled") {
      set((s) => ({ partial: { ...s.partial, [sid]: undefined } }));
      return;
    }
    if (rec.type !== "message_update") return;
    const ev = rec.assistantMessageEvent ?? {};
    const cur = get().partial[sid] ?? { blocks: [] };
    const blocks = cur.blocks.slice();
    const i: number = ev.contentIndex ?? 0;
    switch (ev.type) {
      case "text_start":
        blocks[i] = { type: "text", text: "" };
        break;
      case "text_delta": {
        const b = blocks[i];
        blocks[i] = { type: "text", text: (b?.type === "text" ? b.text : "") + ev.delta };
        break;
      }
      case "thinking_start":
        blocks[i] = { type: "thinking", thinking: "" };
        break;
      case "thinking_delta": {
        const b = blocks[i];
        blocks[i] = { type: "thinking", thinking: (b?.type === "thinking" ? b.thinking : "") + ev.delta };
        break;
      }
      case "toolcall_start":
        blocks[i] = { type: "toolCall", id: ev.id, name: ev.toolName, arguments: {} };
        break;
      case "toolcall_end":
        if (ev.toolCall) blocks[i] = ev.toolCall;
        break;
      default:
        return;
    }
    set((s) => ({ partial: { ...s.partial, [sid]: { blocks } } }));
  },
}));
